/**
 * CommunicationCenter
 *
 * Unified timeline of all customer communications for an order,
 * plus a compose box for sending emails directly.
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  TextField,
  Divider,
  Banner,
  Select,
} from '@shopify/polaris';
import { useState, useCallback, useMemo } from 'react';
import { composeEmail, composeGmailEmail } from '../api/orders';
import type {
  TrackedOrder,
  Approval,
  InboundEmail,
  EmailLog,
  GmailMessageRecord,
  GmailAccount,
} from '../types';

// ============================================
// Timeline Entry Types
// ============================================

interface EmailTracking {
  delivered?: boolean;
  opened?: boolean;
  openedAt?: Date;
  openCount?: number;
  clicked?: boolean;
  bounced?: boolean;
  bounceReason?: string;
}

interface TimelineEntry {
  id: string;
  timestamp: Date;
  type: 'approval_sent' | 'approval_response' | 'inbound_email' | 'email_log' | 'gmail_inbound' | 'gmail_outbound';
  title: string;
  description?: string;
  tone?: 'info' | 'success' | 'warning' | 'attention' | 'critical';
  badgeLabel?: string;
  details?: string;
  tracking?: EmailTracking;
}

// ============================================
// Helpers
// ============================================

function parseTimestamp(ts: string | { _seconds: number; _nanoseconds: number } | null | undefined): Date {
  if (!ts) return new Date(0);
  if (typeof ts === 'object' && '_seconds' in ts) {
    return new Date(ts._seconds * 1000);
  }
  return new Date(ts);
}

const MINUTE_MS = 60000;
const HOUR_MS = 3600000;
const DAY_MS = 86400000;

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < MINUTE_MS) return 'Just now';
  const diffMins = Math.floor(diffMs / MINUTE_MS);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMs / HOUR_MS);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffMs / DAY_MS);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function emailLogTypeLabel(type: string): string {
  switch (type) {
    case 'approval_request': return 'Proof sent';
    case 'status_update': return 'Status update sent';
    case 'production_complete': return 'Production complete notification';
    case 'staff_compose': return 'Staff email sent';
    case 'follow_up': return 'Follow-up notification';
    case 'follow_up_reminder': return 'Follow-up reminder';
    default: return 'Email sent';
  }
}

// ============================================
// Component Props
// ============================================

interface CommunicationCenterProps {
  order: TrackedOrder;
  approvals: Approval[];
  inboundEmails: InboundEmail[];
  emailLogs: EmailLog[];
  gmailMessages: GmailMessageRecord[];
  gmailAccounts?: GmailAccount[];
  onEmailSent: () => void;
}

export default function CommunicationCenter({
  order,
  approvals,
  inboundEmails,
  emailLogs,
  gmailMessages,
  gmailAccounts,
  onEmailSent,
}: CommunicationCenterProps) {
  // Compose state
  const [composing, setComposing] = useState(false);
  const [to, setTo] = useState(order.customerEmail);
  const [subject, setSubject] = useState(`Re: Order ${order.shopifyOrderName}`);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendMethod, setSendMethod] = useState<string>('sendgrid');

  // Build unified timeline
  const timeline = useMemo(() => {
    const entries: TimelineEntry[] = [];

    // Build a map of approvalId → emailLog for tracking data
    const approvalEmailLogs = new Map<string, EmailLog>();
    for (const log of emailLogs) {
      if (log.type === 'approval_request' && log.approvalId) {
        approvalEmailLogs.set(log.approvalId, log);
      }
    }

    // Helper to extract tracking from an email log
    function getTracking(log?: EmailLog): EmailTracking | undefined {
      if (!log) return undefined;
      if (!log.delivered && !log.opened && !log.clicked && !log.bounced) return undefined;
      return {
        delivered: log.delivered,
        opened: log.opened,
        openedAt: log.openedAt ? parseTimestamp(log.openedAt) : undefined,
        openCount: log.openCount,
        clicked: log.clicked,
        bounced: log.bounced,
        bounceReason: log.bounceReason,
      };
    }

    // Approvals → timeline entries
    for (const approval of approvals) {
      const approvalLog = approvalEmailLogs.get(approval.id);
      // Approval sent
      entries.push({
        id: `approval-sent-${approval.id}`,
        timestamp: parseTimestamp(approval.sentAt),
        type: 'approval_sent',
        title: `Proof sent (v${approval.version})`,
        description: `To: ${approval.sentTo}${approval.proofNotes ? `\nNotes: ${approval.proofNotes}` : ''}`,
        tone: 'info',
        badgeLabel: 'Proof',
        tracking: getTracking(approvalLog),
      });

      // Approval response (if any)
      if (approval.status === 'approved' && approval.respondedAt) {
        entries.push({
          id: `approval-approved-${approval.id}`,
          timestamp: parseTimestamp(approval.respondedAt),
          type: 'approval_response',
          title: 'Customer approved proof',
          tone: 'success',
          badgeLabel: 'Approved',
        });
      } else if (approval.status === 'rejected' && approval.respondedAt) {
        entries.push({
          id: `approval-rejected-${approval.id}`,
          timestamp: parseTimestamp(approval.respondedAt),
          type: 'approval_response',
          title: 'Customer requested changes',
          description: approval.rejectionReason || undefined,
          tone: 'critical',
          badgeLabel: 'Changes',
        });
      }
    }

    // Inbound emails → timeline entries
    for (const email of inboundEmails) {
      entries.push({
        id: `inbound-${email.id}`,
        timestamp: parseTimestamp(email.createdAt),
        type: 'inbound_email',
        title: `Customer reply: ${email.subject || '(no subject)'}`,
        description: email.textBody ? email.textBody.substring(0, 150) + (email.textBody.length > 150 ? '...' : '') : undefined,
        tone: 'attention',
        badgeLabel: email.attachmentCount > 0 ? `Reply +${email.attachmentCount} file${email.attachmentCount !== 1 ? 's' : ''}` : 'Reply',
      });
    }

    // Email logs → timeline entries (skip approval_request type since we already show those via approvals)
    for (const log of emailLogs) {
      if (log.type === 'approval_request') continue;
      entries.push({
        id: `log-${log.id || log.messageId || String(Math.random())}`,
        timestamp: parseTimestamp(log.sentAt),
        type: 'email_log',
        title: emailLogTypeLabel(log.type),
        description: log.subject ? `Subject: ${log.subject}` : undefined,
        tone: log.type === 'staff_compose' ? 'info' : undefined,
        badgeLabel: log.type === 'staff_compose' ? 'Sent' : 'Auto',
        tracking: getTracking(log),
      });
    }

    // Gmail messages → timeline entries
    for (const msg of gmailMessages) {
      entries.push({
        id: `gmail-${msg.id}`,
        timestamp: parseTimestamp(msg.date),
        type: msg.direction === 'inbound' ? 'gmail_inbound' : 'gmail_outbound',
        title: msg.direction === 'inbound'
          ? `Email from ${msg.from}: ${msg.subject || '(no subject)'}`
          : `Sent via Gmail: ${msg.subject || '(no subject)'}`,
        description: msg.bodySnippet || undefined,
        tone: msg.direction === 'inbound' ? 'attention' : 'info',
        badgeLabel: msg.direction === 'inbound' ? 'Gmail' : `Sent (${msg.accountEmail})`,
      });
    }

    // Sort by timestamp descending (newest first)
    entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return entries;
  }, [approvals, inboundEmails, emailLogs, gmailMessages]);

  // Handle send
  const handleSend = useCallback(async () => {
    if (!to || !subject || !body) return;

    setSending(true);
    setSendError(null);
    setSendSuccess(false);

    try {
      if (sendMethod !== 'sendgrid') {
        // Send via Gmail
        await composeGmailEmail(order.id, { accountId: sendMethod, to, subject, body });
      } else {
        await composeEmail(order.id, { to, subject, body });
      }
      setSendSuccess(true);
      setBody('');
      setComposing(false);
      onEmailSent();
      // Clear success message after a few seconds
      setTimeout(() => setSendSuccess(false), 3000);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  }, [to, subject, body, order.id, onEmailSent, sendMethod]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Communications
          </Text>
          <Badge tone="info">{String(timeline.length)}</Badge>
        </InlineStack>

        <Divider />

        {/* Success banner */}
        {sendSuccess && (
          <Banner tone="success" onDismiss={() => setSendSuccess(false)}>
            <p>Email sent successfully</p>
          </Banner>
        )}

        {/* Compose box */}
        {composing ? (
          <BlockStack gap="300">
            {gmailAccounts && gmailAccounts.length > 0 && (
              <Select
                label="Send via"
                options={[
                  { label: 'SendGrid (proof@production.sixhats.ca)', value: 'sendgrid' },
                  ...gmailAccounts.map((a) => ({
                    label: `Gmail: ${a.email} (${a.label})`,
                    value: a.id,
                  })),
                ]}
                value={sendMethod}
                onChange={setSendMethod}
              />
            )}
            <TextField
              label="To"
              value={to}
              onChange={setTo}
              autoComplete="email"
            />
            <TextField
              label="Subject"
              value={subject}
              onChange={setSubject}
              autoComplete="off"
            />
            <TextField
              label="Message"
              value={body}
              onChange={setBody}
              multiline={4}
              autoComplete="off"
              placeholder="Type your message..."
            />
            {sendError && (
              <Banner tone="critical" onDismiss={() => setSendError(null)}>
                <p>{sendError}</p>
              </Banner>
            )}
            <InlineStack gap="200">
              <Button variant="primary" onClick={handleSend} loading={sending} disabled={!body.trim()}>
                Send
              </Button>
              <Button onClick={() => { setComposing(false); setSendError(null); }}>
                Cancel
              </Button>
            </InlineStack>
          </BlockStack>
        ) : (
          <Button onClick={() => setComposing(true)} fullWidth>
            Compose Email
          </Button>
        )}

        <Divider />

        {/* Timeline */}
        {timeline.length === 0 ? (
          <Text as="p" tone="subdued">
            No communications yet
          </Text>
        ) : (
          <BlockStack gap="300">
            {timeline.map((entry) => (
              <div key={entry.id} style={{ paddingLeft: 12, borderLeft: '2px solid #e1e3e5' }}>
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    {entry.badgeLabel && (
                      <Badge tone={entry.tone} size="small">
                        {entry.badgeLabel}
                      </Badge>
                    )}
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {entry.title}
                    </Text>
                  </InlineStack>
                  {entry.description && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      <span style={{ whiteSpace: 'pre-line' }}>{entry.description}</span>
                    </Text>
                  )}
                  {entry.tracking && (
                    <InlineStack gap="200" blockAlign="center">
                      {entry.tracking.bounced ? (
                        <Badge tone="critical" size="small">Bounced</Badge>
                      ) : entry.tracking.clicked ? (
                        <Badge tone="success" size="small">Clicked</Badge>
                      ) : entry.tracking.opened ? (
                        <Badge tone="success" size="small">
                          {`Opened${entry.tracking.openCount && entry.tracking.openCount > 1 ? ` (${entry.tracking.openCount}x)` : ''}`}
                        </Badge>
                      ) : entry.tracking.delivered ? (
                        <Badge size="small">Delivered</Badge>
                      ) : null}
                      {entry.tracking.openedAt && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {formatRelativeTime(entry.tracking.openedAt)}
                        </Text>
                      )}
                    </InlineStack>
                  )}
                  <Text as="span" variant="bodySm" tone="subdued">
                    {formatRelativeTime(entry.timestamp)}
                  </Text>
                </BlockStack>
              </div>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
